import React, { useEffect, useMemo } from 'react';

import { ConfigProvider as AntConfigProvider } from 'antd';
import { Provider, getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { createRoot } from 'react-dom/client';
import { EditEvent } from '../components/EditEvent';
import { ReadOnlyEvent } from '../components/ReadOnlyEvent';
import ResaCalendar, { ResaCalendarEvent } from '../components/ResaCalendar';
import { focusResaAtom } from '../store/core';
import {
	ResaLocation,
	allLocationsAtom,
	locationAtom,
	locationFrequentation,
	locationParser,
} from '../store/location';
import {
	allReservationsAtom,
	// locationFrequentation,
	locationReserationsAtom,
	mergeReservations,
	userReservationsAtom,
} from '../store/reservation';
import { allUsersAtom } from '../store/user';
import { antFocusTheme } from '../styles/antd-theme-focus';
import './location-calendar.scss';
import { antLocale, I18nProvider } from '../core/i18n';

(() => {
	document.addEventListener('DOMContentLoaded', async () => {
		const root = document.querySelector('.focus_resa_location_calendar');
		if (!root)
			throw new Error(
				'Unable to find node .focus_resa_location_calendar to render to',
			);

		try {
			const raw = JSON.parse(root.getAttribute('data-location') ?? '');
			const loc = locationParser.parse(raw);

			const Hydrator = (props: { children: React.ReactNode }) => {
				const setLocation = useSetAtom(locationAtom(loc.ID));
				useEffect(() => setLocation(loc), []);
				return props.children;
			};

			createRoot(root).render(
				<React.StrictMode>
					<Provider store={getDefaultStore()}>
						<Hydrator>
							<AntConfigProvider
								theme={antFocusTheme}
								locale={await antLocale()}
							>
								<I18nProvider>
									<LocationCalendar location={loc} />
								</I18nProvider>
							</AntConfigProvider>
						</Hydrator>
					</Provider>
				</React.StrictMode>,
			);
		} catch (e) {
			console.error(e.issues, e.format());
		}
	});
})();

const LocationCalendar = (props: { location: ResaLocation }) => {
	const focusResa = useAtomValue(focusResaAtom);
	const userReservations = useAtomValue(
		userReservationsAtom(focusResa.loggedInUser),
	);
	const locationReservations = useAtomValue(
		locationReserationsAtom(props.location.ID),
	);
	const setReservations = useSetAtom(allReservationsAtom);
	const setParticiapants = useSetAtom(allUsersAtom);
	const setLocations = useSetAtom(allLocationsAtom);
	const freq = useAtomValue(locationFrequentation(props.location.ID));

	useEffect(() => {
		(async () => {
			const [userResa, locResa] = await Promise.all([
				focusResa.getUserReservations({ userId: focusResa.loggedInUser }),
				focusResa.getLocationReservations({ locationId: props.location.ID }),
			]);
			setReservations((all) =>
				mergeReservations(
					all,
					mergeReservations(Object.values(locResa), Object.values(userResa)),
				),
			);
		})();
	}, [props.location]);

	useEffect(() => {
		(async () => {
			const [locations, users] = await Promise.all([
				focusResa.getLocations(),
				focusResa.getParticipants(),
			]);
			setLocations(locations);
			setParticiapants(users);
		})();
	}, []);

	const [reservations, sideReservations] = useMemo(() => {
		if (focusResa.isElevatedUser) {
			return [locationReservations, []];
		}
		return [
			userReservations.filter((r) => r.locationId == props.location.ID),
			userReservations.filter((r) => r.locationId != props.location.ID),
		];
	}, [focusResa, userReservations, locationReservations]);

	return (
		<ResaCalendar
			reservations={reservations}
			sideReservations={sideReservations}
			availabilitySlots={props.location.openHours ?? []}
			defaultLocation={props.location}
			defaultUsers={[focusResa.loggedInUser]}
			showCancelled={false}
			modalContainer={document.querySelector('.focus_resa_location_calendar')!}
			displayOnlyOpen={true}
			affluence={freq}
			EventEditor={ReadOrEdit}
		/>
	);
};

const ReadOrEdit = (props: {
	event: ResaCalendarEvent;
	close: () => void;
	commit: (e: ResaCalendarEvent) => void;
}) => {
	const location = useAtomValue(
		locationAtom(props.event.resource.locationId ?? 0),
	);

	if (
		props.event.id != 'new' &&
		(props.event.resource.anonymized ||
			(location?.requiresApproval && props.event.resource.approved != null))
	)
		return <ReadOnlyEvent event={props.event} close={props.close} />;

	return (
		<EditEvent event={props.event} close={props.close} commit={props.commit} />
	);
};
